import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

// Backend routes. Templates: /HandoutTemplates. Deliveries: /HandoutDeliveries (PascalCase, separate controller).
const HANDOUT_TEMPLATES_BASE = process.env.REACT_APP_HANDOUT_TEMPLATES_PATH || '/HandoutTemplates';
const HANDOUT_DELIVERIES_BASE = process.env.REACT_APP_HANDOUT_DELIVERIES_PATH || '/HandoutDeliveries';

const normalizeDelivery = (delivery) => {
  if (!delivery || typeof delivery !== 'object') return delivery;
  return {
    ...delivery,
    templateUpdatedAt: delivery.templateUpdatedAt ?? delivery.TemplateUpdatedAt ?? null,
    deliveryDetails: delivery.deliveryDetails ?? delivery.DeliveryDetails ?? null
  };
};

const normalizeDeliveryList = (data) => {
  if (!Array.isArray(data)) return data;
  return data.map(normalizeDelivery);
};

export const handoutApi = {
  getTemplates: async (filters = {}) => {
    // Backend accepts `includeArchived` (bool), not `status`. Translate here.
    const { status, ...rest } = filters;
    const params = new URLSearchParams(rest);
    const wantArchived = status === 'Archived';
    if (wantArchived) params.set('includeArchived', 'true');

    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}?${params}`,
      { headers: getAuthHeaders() }
    );
    const data = await handleResponse(response);

    // When fetching archived, backend returns all (active + archived); filter to archived only.
    if (wantArchived && Array.isArray(data)) {
      return data.filter(t => t.isArchived);
    }
    return data;
  },

  getTemplateById: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${referenceId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  createTemplate: async (data) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  updateTemplate: async (referenceId, data) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${referenceId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  updateContent: async (referenceId, data) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${referenceId}/content`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  uploadFile: async (referenceId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('velaToken');
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${referenceId}/file`,
      {
        method: 'POST',
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      }
    );
    return handleResponse(response);
  },

  archiveTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${referenceId}/archive`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  deleteTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${referenceId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  deliverToClient: async (data) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return normalizeDelivery(await handleResponse(response));
  },

  getClientDeliveries: async (clientReferenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}/client/${clientReferenceId}`,
      { headers: getAuthHeaders() }
    );
    return normalizeDeliveryList(await handleResponse(response));
  },

  getMyDeliveries: async () => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}/my-deliveries`,
      { headers: getAuthHeaders() }
    );
    const data = await handleResponse(response);
    // Backend may return array directly or wrapped (e.g. { items: [...] })
    if (Array.isArray(data)) return normalizeDeliveryList(data);
    if (data?.items && Array.isArray(data.items)) return normalizeDeliveryList(data.items);
    if (data?.deliveries && Array.isArray(data.deliveries)) return normalizeDeliveryList(data.deliveries);
    if (data?.data && Array.isArray(data.data)) return normalizeDeliveryList(data.data);
    return [];
  },

  getDeliveryById: async (deliveryReferenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}/${deliveryReferenceId}`,
      { headers: getAuthHeaders() }
    );
    return normalizeDelivery(await handleResponse(response));
  },

  markAsViewed: async (deliveryReferenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}/${deliveryReferenceId}/mark-viewed`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  deleteDelivery: async (deliveryReferenceId) => {
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}/${deliveryReferenceId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  cleanupDuplicateDeliveries: async ({ organizationId } = {}) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organizationId', organizationId);
    const query = params.toString();
    const response = await fetch(
      `${config.api.url}${HANDOUT_DELIVERIES_BASE}/cleanup-duplicates${query ? `?${query}` : ''}`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  // Returns raw blob for creating an object URL for preview/download
  getTemplateFile: async (templateReferenceId) => {
    const token = localStorage.getItem('velaToken');
    const response = await fetch(
      `${config.api.url}${HANDOUT_TEMPLATES_BASE}/${templateReferenceId}/file`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('File download failed');
    return response.blob();
  }
};
